import ProductImage from "@/store/components/ProductImage";
import PriceTag from "@/store/components/PriceTag";
import BuyButton from "@/store/components/BuyButton";
import PurchasedBadge, { getDescText } from "@/store/components/PurchasedBadge";
import { isBundleCategory } from "@/store/constants";
import type { Coupon, Product } from "@/store/types";

interface ProductDetailProps {
  product: Product;
  isPurchased: boolean;
  isMember: boolean;
  coupons: Coupon[];
  busy: boolean;
  processingCode: string | null;
  onPurchase: (product: Product) => void;
  onAddToCart: (product: Product) => void;
  /** In the quick-view modal, media stacks above the info and the image isn't cropped. */
  compact?: boolean;
}

/** Full single-product layout: media beside (or above, when compact) title/description/CTA. */
export default function ProductDetail({
  product,
  isPurchased,
  isMember,
  coupons,
  busy,
  processingCode,
  onPurchase,
  onAddToCart,
  compact,
}: ProductDetailProps) {
  return (
    <div
      className={
        compact
          ? "agd:flex agd:flex-col"
          : "agd:flex agd:flex-col agd:overflow-hidden agd:rounded-[10px] agd:border agd:border-border agd:bg-card agd:shadow-sm md:agd:flex-row"
      }
    >
      <div className={compact ? "agd:pb-4" : "agd:p-4 md:agd:max-w-[420px] md:agd:flex-[0_0_40%]"}>
        <ProductImage
          product={product}
          className={
            compact
              ? "agd:block agd:max-h-[300px] agd:w-full agd:rounded-lg agd:object-contain"
              : "agd:block agd:h-[200px] agd:w-full agd:rounded-lg agd:object-cover md:agd:h-[280px]"
          }
        />
      </div>
      <div className={compact ? "agd:flex agd:flex-col" : "agd:flex agd:flex-1 agd:flex-col agd:px-7 agd:py-6"}>
        <div className="agd:mb-1.5 agd:flex agd:items-center agd:justify-between agd:gap-2">
          <span className="agd:text-[0.72rem] agd:font-semibold agd:uppercase agd:tracking-wide agd:text-muted-foreground">
            {product.ProductCode}
          </span>
          <PurchasedBadge show={isPurchased && !isBundleCategory(product.Category)} />
        </div>
        <h2 className="agd:mb-3 agd:text-2xl agd:font-bold agd:leading-tight agd:text-foreground">
          {product.Title ?? ""}
        </h2>
        <div className="agd:mb-5 agd:whitespace-pre-line agd:text-[0.92rem] agd:leading-relaxed agd:text-[#4a5568]">
          {getDescText(product.Description)}
        </div>
        <div className="agd:mt-auto agd:max-w-[280px]">
          <PriceTag product={product} isMember={isMember} />
          <BuyButton
            product={product}
            isPurchased={isPurchased}
            coupons={coupons}
            isMember={isMember}
            busy={busy}
            processing={processingCode === product.ProductCode}
            onPurchase={onPurchase}
            onAddToCart={onAddToCart}
          />
        </div>
      </div>
    </div>
  );
}
